import React, { useEffect, useState } from "react";
import Sidebar from "../admin/Sidebar";
import TopBar from "../components/TopBar";
import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/css/ManageContacts.css";
import { Button, Modal, Table, Form, Pagination, Spinner, Alert } from "react-bootstrap";

const ManageContacts = () => {
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const [loadingReply, setLoadingReply] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertVariant, setAlertVariant] = useState("success");

  const itemsPerPage = 10;

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/contact`);
      const data = await response.json();
      setMessages(data);
      setFilteredMessages(data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this message?")) {
      await fetch(`${process.env.REACT_APP_API_URL}/contact/${id}`, { method: "DELETE" });
      fetchMessages();
    }
  };

  const handleReply = (message) => {
    setSelectedMessage(message);
    setReplyText("");
    setShowModal(true);
    setAlertMessage(null);
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      setAlertVariant("danger");
      setAlertMessage("Please enter a reply message.");
      return;
    }

    try {
      setLoadingReply(true);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/contact/reply/${selectedMessage._id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reply: replyText }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setAlertVariant("success");
        setAlertMessage(data.message || "Reply sent successfully!");
        setReplyText("");

        setTimeout(() => {
          setShowModal(false);
          setSelectedMessage(null);
          setAlertMessage(null);
        }, 1500);
      } else {
        setAlertVariant("danger");
        setAlertMessage(data.message || "Failed to send reply.");
      }
    } catch (error) {
      console.error("Reply Error:", error);
      setAlertVariant("danger");
      setAlertMessage("Something went wrong. Please try again.");
    } finally {
      setLoadingReply(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const filtered = messages.filter(
      (msg) =>
        msg.name.toLowerCase().includes(query.toLowerCase()) ||
        msg.email.toLowerCase().includes(query.toLowerCase()) ||
        msg.message.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredMessages(filtered);
    setCurrentPage(1);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentMessages = filteredMessages.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredMessages.length / itemsPerPage);

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar setIsSidebarOpen={setIsSidebarOpen} />

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          marginLeft: window.innerWidth > 768 ? (isSidebarOpen ? "250px" : "80px") : "0px",
          padding: "20px",
        }}
      >
        <TopBar />

        <div className="container mt-4 manage-contacts-container">

          {/* SEARCH BAR */}
          <div className="d-flex justify-content-end mb-3 flex-wrap">
            <Form.Control
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="manage-contacts-search"
            />
          </div>

          {/* TABLE WRAPPER FOR MOBILE */}
          <div className="table-responsive">

            <Table striped bordered hover className="shadow-sm manage-contacts-table">
              <thead className="table-dark">
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Message</th>
                  <th style={{ minWidth: "160px" }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {currentMessages.map((msg) => (
                  <tr key={msg._id}>
                    <td>{msg.name}</td>
                    <td>{msg.email}</td>
                    <td className="message-cell">{msg.message}</td>

                    <td className="action-buttons">
                      <div className="action-buttons-wrapper">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleReply(msg)}
                        >
                          Reply
                        </Button>

                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(msg._id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>

                  </tr>
                ))}

                {currentMessages.length === 0 && (
                  <tr>
                    <td colSpan="4" className="text-center">
                      No messages found.
                    </td>
                  </tr>
                )}

              </tbody>
            </Table>

          </div>

          {totalPages > 1 && (
            <Pagination className="justify-content-center manage-contacts-pagination">
              {[...Array(totalPages)].map((_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={currentPage === i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
            </Pagination>
          )}

          {/* REPLY MODAL */}

          <Modal show={showModal} onHide={() => setShowModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Reply to Message</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              {selectedMessage && (
                <>
                  <p><strong>Name:</strong> {selectedMessage.name}</p>
                  <p><strong>Email:</strong> {selectedMessage.email}</p>
                  <p><strong>Message:</strong> {selectedMessage.message}</p>

                  <Form.Control
                    as="textarea"
                    rows={4}
                    className="mt-3"
                    placeholder="Type your reply here..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />

                  {alertMessage && (
                    <Alert variant={alertVariant} className="mt-3">
                      {alertMessage}
                    </Alert>
                  )}
                </>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Close
              </Button>

              <Button
                variant="success"
                onClick={handleSendReply}
                disabled={loadingReply}
              >
                {loadingReply ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Sending...
                  </>
                ) : (
                  "Send Reply"
                )}
              </Button>
            </Modal.Footer>

          </Modal>

        </div>
      </div>
    </div>
  );
};

export default ManageContacts;